import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import LifeAtHipro from '../components/site/LifeAtHipro';
import EmployeeStories from '../components/site/EmployeeStories';
import CampusDriveForm from '../components/site/CampusDriveForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { publicJobs, publicJobDetail, publicApply, applyProfile, oneClickApply } from '../lib/hrmsApi';
import { usePublicAuth } from '../context/PublicAuth';
import { useToast } from '../hooks/use-toast';
import Seo, { SITE_URL, DEFAULT_OG_IMAGE, breadcrumbLd } from '../components/Seo';
import {
  Briefcase, MapPin, Clock, ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  Search, X, Share2, Copy, Users2, Sparkles, Zap,
} from 'lucide-react';

// -------- Shared: ShareRefer Widget --------
const ShareRefer = ({ job, refCode }) => {
  const { toast } = useToast();
  const shareUrl = useMemo(() => {
    const base = `${window.location.origin}/careers/${job.job_id}`;
    return refCode ? `${base}?ref=${encodeURIComponent(refCode)}` : base;
  }, [job.job_id, refCode]);

  const shareText = `Check out this opening at Hipro Technologies: ${job.title}${job.location ? ' — ' + job.location : ''}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copied', description: refCode ? 'Your referral code is included.' : '' });
    } catch {
      toast({ title: 'Could not copy', description: 'Please copy manually.' });
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: job.title, text: shareText, url: shareUrl }); } catch {}
    } else copyLink();
  };

  const encoded = encodeURIComponent(`${shareText}\n${shareUrl}`);
  const waHref = `https://wa.me/?text=${encoded}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(`Job opening: ${job.title}`)}&body=${encoded}`;

  return (
    <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4" data-testid="share-refer-widget">
      <div className="flex items-center gap-2 text-orange-700 font-semibold text-sm">
        <Users2 className="h-4 w-4" /> Refer a friend
      </div>
      <p className="mt-1 text-xs text-slate-600">
        {refCode
          ? <>Your referral code <b className="font-mono">{refCode}</b> is attached. Anyone who applies via your link is credited to you.</>
          : <>Share this job with friends. Employees — grab your unique link from <Link to="/hrms/me" className="text-orange-600 underline">HRMS → My Referrals</Link> to earn rewards.</>}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={copyLink} data-testid="share-copy-btn" className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs font-semibold hover:border-orange-300"><Copy className="h-3.5 w-3.5" /> Copy link</button>
        <button onClick={nativeShare} data-testid="share-native-btn" className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs font-semibold hover:border-orange-300"><Share2 className="h-3.5 w-3.5" /> Share</button>
        <a href={waHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs font-semibold hover:border-orange-300">WhatsApp</a>
        <a href={liHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs font-semibold hover:border-orange-300">LinkedIn</a>
        <a href={mailHref} className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs font-semibold hover:border-orange-300">Email</a>
      </div>
    </div>
  );
};

// -------- Careers listing with search + filters --------
export const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [loc, setLoc] = useState('');
  const [type, setType] = useState('');
  const [params] = useSearchParams();
  const refCode = params.get('ref') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
    publicJobs().then(setJobs).finally(() => setLoading(false));
  }, []);

  const departments = useMemo(() => Array.from(new Set(jobs.map(j => j.department).filter(Boolean))).sort(), [jobs]);
  const locations = useMemo(() => Array.from(new Set(jobs.map(j => j.location).filter(Boolean))).sort(), [jobs]);
  const types = useMemo(() => Array.from(new Set(jobs.map(j => j.employment_type).filter(Boolean))).sort(), [jobs]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return jobs.filter(j => {
      if (dept && j.department !== dept) return false;
      if (loc && j.location !== loc) return false;
      if (type && j.employment_type !== type) return false;
      if (!needle) return true;
      const hay = [
        j.title, j.department, j.location, j.employment_type, j.work_mode,
        j.description, j.responsibilities, j.requirements,
        ...(j.skills || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [jobs, q, dept, loc, type]);

  const clear = () => { setQ(''); setDept(''); setLoc(''); setType(''); };
  const hasFilter = q || dept || loc || type;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="Careers at Hipro Technologies | Open Roles in Tech, AI & Marketing"
        description="Join our global team across USA, UK, UAE, Netherlands, Malaysia, Hong Kong and India. Browse open roles in engineering, AI, design and marketing — every application reviewed within 48 hours."
        path="/careers"
      />
      <Navbar />
      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Careers</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold">Build the future with Hipro Technologies</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Join our global team across USA, UK, UAE, Netherlands, Malaysia, Hong Kong and India. Every application is reviewed within 48 hours with AI-assisted screening.</p>
          {refCode && (
            <div className="mt-5 inline-flex items-center gap-2 text-xs bg-orange-500/20 border border-orange-400/30 text-orange-100 rounded-full px-3 py-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Referred by employee <b className="font-mono">{refCode}</b>
            </div>
          )}
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 grid gap-3 lg:grid-cols-[1fr_auto_auto_auto_auto]">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="careers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, skill, keyword…" className="pl-9 h-11" />
          </div>
          <select data-testid="careers-filter-department" value={dept} onChange={(e) => setDept(e.target.value)} className="h-11 rounded-md border px-3 text-sm bg-white">
            <option value="">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="careers-filter-location" value={loc} onChange={(e) => setLoc(e.target.value)} className="h-11 rounded-md border px-3 text-sm bg-white">
            <option value="">All locations</option>
            {locations.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select data-testid="careers-filter-type" value={type} onChange={(e) => setType(e.target.value)} className="h-11 rounded-md border px-3 text-sm bg-white">
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {hasFilter && (
            <Button data-testid="careers-clear-filters" variant="outline" onClick={clear} className="h-11"><X className="h-4 w-4 mr-1" /> Clear</Button>
          )}
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-xs text-slate-500 mb-4" data-testid="careers-count">
            {loading ? 'Loading…' : `${filtered.length} open ${filtered.length === 1 ? 'role' : 'roles'}${hasFilter ? ' matching your filters' : ''}`}
          </div>
          {loading ? (
            <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 border border-dashed rounded-2xl text-slate-500" data-testid="careers-empty">
              {jobs.length === 0 ? 'No open positions right now. Please check back soon.' : 'No roles match your filters. Try clearing them.'}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filtered.map((j) => (
                <Link
                  key={j.job_id}
                  to={`/careers/${j.job_id}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ''}`}
                  data-testid={`job-card-${j.job_id}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{j.job_id}</span>
                    {j.department && <span>{j.department}</span>}
                  </div>
                  <h3 className="mt-2 text-xl font-bold text-slate-900 group-hover:text-orange-600">{j.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                    {j.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {j.location}</span>}
                    {j.employment_type && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {j.employment_type}</span>}
                    {j.work_mode && <span>{j.work_mode}</span>}
                    {j.experience && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {j.experience}</span>}
                  </div>
                  {j.description && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{j.description}</p>}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-orange-600 font-semibold text-sm inline-flex items-center gap-1">View details <ArrowRight className="h-4 w-4" /></div>
                    <button
                      type="button"
                      data-testid={`job-share-${j.job_id}`}
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const url = `${window.location.origin}/careers/${j.job_id}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ''}`;
                        if (navigator.share) navigator.share({ title: j.title, url }).catch(() => {});
                        else navigator.clipboard?.writeText(url);
                      }}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-orange-600"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Refer
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <LifeAtHipro />
      <EmployeeStories />
      <CampusDriveForm />
      <Footer />
      <ChatWidget />
    </div>
  );
};

// -------- Job detail with apply + share/refer --------
export const JobDetailPage = () => {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const refCode = params.get('ref') || '';
  const nav = useNavigate();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const { user } = usePublicAuth();
  const [profile, setProfile] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const [f, setF] = useState({
    full_name: '', email: '', mobile: '', alt_mobile: '', dob: '', gender: '',
    current_location: '', preferred_location: '', linkedin: '', portfolio: '',
    total_experience: '', relevant_experience: '', current_company: '', current_designation: '',
    current_ctc: '', expected_ctc: '', notice_period: '',
    highest_qualification: '', university: '', passing_year: '',
    skills: '', cover_letter: '', consent: true, resume_name: '', resume_b64: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    publicJobDetail(jobId).then(setJob).catch(() => setNotFound(true));
  }, [jobId]);

  useEffect(() => {
    if (!user) { setProfile(null); return; }
    applyProfile().then((res) => {
      setProfile(res);
      const p = res.profile || {};
      setF(prev => {
        const merged = { ...prev };
        Object.keys(p).forEach(k => {
          if (k in merged && !merged[k] && p[k]) merged[k] = p[k];
        });
        return merged;
      });
    }).catch(() => setProfile(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const quickApply = async () => {
    setQuickBusy(true);
    try {
      const res = await oneClickApply(jobId, refCode);
      setDone(res);
      toast({ title: 'Application submitted!', description: 'Your saved profile and resume were used. Our AI is reviewing it now.' });
    } catch (err) {
      toast({ title: 'Could not apply', description: err?.response?.data?.detail || 'Please try the full form.' });
    } finally { setQuickBusy(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast({ title: 'File too large', description: 'Please keep resume under 2.5MB.' }); return; }
    const r = new FileReader();
    r.onload = () => setF({ ...f, resume_name: file.name, resume_b64: r.result });
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.email || !f.mobile) { toast({ title: 'Please fill name, email and mobile.' }); return; }
    setSubmitting(true);
    try {
      const res = await publicApply(jobId, f, refCode);
      setDone(res);
      toast({ title: 'Application received', description: refCode ? 'Thanks for applying through an employee referral!' : 'Our AI is already reviewing your profile.' });
    } catch (err) {
      toast({ title: 'Could not submit', description: err?.response?.data?.detail || '' });
    } finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto px-5 py-40 text-center"><h1 className="text-3xl font-extrabold">Job not found</h1><Button onClick={() => nav('/careers')} className="mt-6 bg-orange-500 hover:bg-orange-600">Back to careers</Button></div><Footer /></div>
  );
  if (!job) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto px-5 py-40 text-center text-slate-500">Loading…</div></div>;

  if (done) return (
    <div className="min-h-screen bg-white"><Navbar />
      <section className="pt-28 pb-16 bg-slate-950 text-white">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <div className="h-16 w-16 rounded-full bg-emerald-500 mx-auto flex items-center justify-center"><CheckCircle2 className="h-8 w-8" /></div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold">Application received!</h1>
          <p className="mt-3 text-slate-300">Your candidate ID is <b className="text-orange-400">{done.candidate_id}</b>. Our AI is scoring your profile now and our team will reach out shortly.</p>
          <Button onClick={() => nav('/careers')} className="mt-6 bg-orange-500 hover:bg-orange-600">Browse more roles</Button>
        </div>
      </section>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title={`${job.title} — ${job.location || 'Remote'} | Careers at Hipro Technologies`}
        description={(job.description || `Apply for ${job.title} at Hipro Technologies.`).slice(0, 160)}
        path={`/careers/${job.job_id}`}
        jsonLd={[breadcrumbLd([
          { name: 'Home', path: '/' },
          { name: 'Careers', path: '/careers' },
          { name: job.title, path: `/careers/${job.job_id}` },
        ]), {
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: job.title,
          description: [job.description, job.responsibilities, job.requirements].filter(Boolean).join('\n\n') || job.title,
          datePosted: job.created_at,
          employmentType: (job.employment_type || 'FULL_TIME').toUpperCase().replace('-', '_'),
          hiringOrganization: { '@type': 'Organization', name: 'Hipro Technologies', sameAs: SITE_URL, logo: DEFAULT_OG_IMAGE },
          jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: job.location || 'Remote', addressCountry: 'IN' } },
          ...(String(job.work_mode || '').toLowerCase().includes('remote') ? { jobLocationType: 'TELECOMMUTE' } : {}),
          directApply: true,
        }]}
      />
      <Navbar />
      <section className="pt-28 pb-8 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Link to={`/careers${refCode ? `?ref=${encodeURIComponent(refCode)}` : ''}`} className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400"><ArrowLeft className="h-4 w-4" /> Back to careers</Link>
          <div className="mt-4 flex items-center gap-2 text-xs"><span className="font-mono bg-white/10 border border-white/10 rounded px-2 py-0.5">{job.job_id}</span>{job.department && <span className="text-slate-300">{job.department}</span>}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold">{job.title}</h1>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-300">
            {job.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-orange-400" /> {job.location}</span>}
            {job.employment_type && <span>{job.employment_type}</span>}{job.work_mode && <span>{job.work_mode}</span>}{job.experience && <span>{job.experience}</span>}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {job.description && (<div><h2 className="text-lg font-bold">About the role</h2><p className="mt-2 text-slate-700 whitespace-pre-line">{job.description}</p></div>)}
            {job.responsibilities && (<div><h2 className="text-lg font-bold">Responsibilities</h2><p className="mt-2 text-slate-700 whitespace-pre-line">{job.responsibilities}</p></div>)}
            {job.requirements && (<div><h2 className="text-lg font-bold">Requirements</h2><p className="mt-2 text-slate-700 whitespace-pre-line">{job.requirements}</p></div>)}
            {job.skills?.length > 0 && (<div><h2 className="text-lg font-bold">Skills</h2><div className="mt-2 flex flex-wrap gap-2">{job.skills.map(s => <span key={s} className="text-xs bg-slate-100 border rounded-full px-2.5 py-1">{s}</span>)}</div></div>)}
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border p-5 sticky-safe">
              {job.salary_range && <div className="text-sm"><span className="text-slate-500">Salary:</span> <b>{job.salary_range}</b></div>}
              {job.qualification && <div className="text-sm mt-1"><span className="text-slate-500">Qualification:</span> {job.qualification}</div>}
              <div className="text-sm mt-1"><span className="text-slate-500">Openings:</span> {job.openings}</div>
              {refCode && <div className="mt-3 text-xs inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 rounded-full px-2 py-1"><Sparkles className="h-3 w-3" /> Referral: <span className="font-mono">{refCode}</span></div>}
              {user && profile?.has_previous ? (
                <>
                  <Button data-testid="quick-apply-btn" onClick={quickApply} disabled={quickBusy} className="mt-4 w-full bg-orange-500 hover:bg-orange-600">
                    {quickBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Zap className="h-4 w-4 mr-1.5" /> Quick apply as {(profile.profile?.full_name || user.name || 'you').split(' ')[0]}</>)}
                  </Button>
                  <div className="mt-2 text-[11px] text-slate-500 text-center">
                    Uses your saved profile{profile.has_resume ? ` + resume (${profile.profile?.resume_name})` : ''} ·{' '}
                    <button type="button" data-testid="full-form-link" onClick={() => setApplying(true)} className="text-orange-600 hover:underline font-semibold">use full form</button>
                  </div>
                </>
              ) : (
                <>
                  <Button data-testid="apply-now-btn" onClick={() => setApplying(true)} className="mt-4 w-full bg-orange-500 hover:bg-orange-600">Apply now</Button>
                  {user ? (
                    <div className="mt-2 text-[11px] text-slate-500 text-center" data-testid="prefill-hint">Signed in as {user.email} — your details are prefilled</div>
                  ) : (
                    <div className="mt-2 text-[11px] text-slate-500 text-center">
                      <Link to="/login" data-testid="signin-oneclick-link" className="text-orange-600 hover:underline font-semibold">Sign in</Link> for one-click apply
                    </div>
                  )}
                </>
              )}
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1"><Sparkles className="h-3 w-3 text-orange-500" /> AI-screened within minutes</div>
            </div>
            <ShareRefer job={job} refCode={refCode} />
          </div>
        </div>
      </section>

      {applying && (
        <section className="pb-16">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <form onSubmit={submit} className="rounded-2xl border p-6 sm:p-8 space-y-4" data-testid="apply-form">
              <h2 className="text-xl font-extrabold">Apply for {job.title}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Full name *</Label><Input data-testid="apply-full-name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></div>
                <div><Label>Email *</Label><Input data-testid="apply-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><Label>Mobile *</Label><Input data-testid="apply-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
                <div><Label>Alt mobile</Label><Input value={f.alt_mobile} onChange={(e) => setF({ ...f, alt_mobile: e.target.value })} /></div>
                <div><Label>Current location</Label><Input value={f.current_location} onChange={(e) => setF({ ...f, current_location: e.target.value })} /></div>
                <div><Label>Preferred location</Label><Input value={f.preferred_location} onChange={(e) => setF({ ...f, preferred_location: e.target.value })} /></div>
                <div><Label>LinkedIn</Label><Input value={f.linkedin} onChange={(e) => setF({ ...f, linkedin: e.target.value })} /></div>
                <div><Label>Portfolio / GitHub</Label><Input value={f.portfolio} onChange={(e) => setF({ ...f, portfolio: e.target.value })} /></div>
                <div><Label>Total experience</Label><Input value={f.total_experience} onChange={(e) => setF({ ...f, total_experience: e.target.value })} placeholder="3 years" /></div>
                <div><Label>Relevant experience</Label><Input value={f.relevant_experience} onChange={(e) => setF({ ...f, relevant_experience: e.target.value })} /></div>
                <div><Label>Current company</Label><Input value={f.current_company} onChange={(e) => setF({ ...f, current_company: e.target.value })} /></div>
                <div><Label>Current designation</Label><Input value={f.current_designation} onChange={(e) => setF({ ...f, current_designation: e.target.value })} /></div>
                <div><Label>Current CTC</Label><Input value={f.current_ctc} onChange={(e) => setF({ ...f, current_ctc: e.target.value })} /></div>
                <div><Label>Expected CTC</Label><Input value={f.expected_ctc} onChange={(e) => setF({ ...f, expected_ctc: e.target.value })} /></div>
                <div><Label>Notice period</Label><Input value={f.notice_period} onChange={(e) => setF({ ...f, notice_period: e.target.value })} /></div>
                <div><Label>Highest qualification</Label><Input value={f.highest_qualification} onChange={(e) => setF({ ...f, highest_qualification: e.target.value })} /></div>
                <div><Label>University / College</Label><Input value={f.university} onChange={(e) => setF({ ...f, university: e.target.value })} /></div>
                <div><Label>Passing year</Label><Input value={f.passing_year} onChange={(e) => setF({ ...f, passing_year: e.target.value })} /></div>
              </div>
              <div><Label>Key skills</Label><Input value={f.skills} onChange={(e) => setF({ ...f, skills: e.target.value })} placeholder="React, Node, AWS" /></div>
              <div><Label>Cover letter</Label><Textarea rows={4} value={f.cover_letter} onChange={(e) => setF({ ...f, cover_letter: e.target.value })} /></div>
              <div><Label>Resume (PDF/DOCX, up to 2.5MB)</Label><input type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="mt-1 block text-sm" />{f.resume_name && <div className="mt-1 text-xs text-emerald-600">Attached: {f.resume_name}</div>}</div>
              <label className="flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} /> I consent to Hipro Technologies storing my information for recruitment purposes.</label>
              <Button data-testid="apply-submit-btn" type="submit" disabled={submitting} className="w-full h-11 bg-orange-500 hover:bg-orange-600">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit application'}</Button>
            </form>
          </div>
        </section>
      )}

      <Footer />
      <ChatWidget />
    </div>
  );
};
